//@ts-check
//@ts-ignore
import { component, pure, __ } from 'riot';
import { evaluateAttributeExpressions } from '@riotjs/util/misc';
import { cleanNode, DOMattributesToObject, removeChild } from '@riotjs/util/dom';
import { callOrAssign } from '@riotjs/util/functions';
import expressionTypes from '@riotjs/util/expression-types';
import { shallowEqual } from '../utils/objects';
const { createExpression } = __.DOMBindings;

let store = null;
let options = null;
let runError = true;

function registerStore(_store, _options = {}) {
  store = _store;
  options = _options;
}

function evaluateInitialProps(element, initialProps = {}) {
  return {
    ...DOMattributesToObject(element),
    ...callOrAssign(initialProps)
  };
}

function createAttributeBindings(node, attributes = []) {
  const expressions = attributes.map(a => createExpression(node, a));
  const binding = {};
  const mapFunction = method => scope => {
    expressions.forEach(e => e[method](scope));
    return binding;
  };
  return Object.assign(binding, {
    expressions,
    mount: mapFunction('mount'),
    update: mapFunction('update'),
    unmount: mapFunction('unmount')
  });
}

const attr = {
  type: expressionTypes.ATTRIBUTE,
  name: null,
  evaluate: scope => ({ ...scope.state, ...scope.actions })
};

const wrapMapToProps = (mapToProps) => {
  if (mapToProps == null) {
    return null;
  }

  const proxy = function (stateOrDispatch, ownProps) {
    return proxy.mapToProps(stateOrDispatch, ownProps);
  };

  proxy.mapToProps = (stateOrDispatch, ownProps) => {
    let props = mapToProps(stateOrDispatch, ownProps);
    if (typeof props === 'function') {
      proxy.mapToProps = props;
      props = proxy(stateOrDispatch, ownProps);
    }
    return props;
  };

  return proxy;
};

function connect(Component, _mapStateToProps, _mapDispatchToProps) {
  const mapStateToProps = wrapMapToProps(_mapStateToProps);
  const mapDispatchToProps = wrapMapToProps(_mapDispatchToProps);
  return {
    name: Component.name,
    exports: pure(
      ({ slots, attributes, props }) => ({
        el: null,
        component: null,

        state: {},
        actions: {},
        props: {},
        attributes: null,
        unsubscribe: null,

        isMounted: false,
        container: true,

        getMappedState() {
          const state = store.getState();
          return mapStateToProps ? mapStateToProps(state, this.props) : this.state;
        },

        getMappedActions() {
          return mapDispatchToProps && mapDispatchToProps.length === 2 ? mapDispatchToProps(store.dispatch, this.props) : this.actions;
        },

        mount(el, parentScope) {
          try {
            this.el = el;
            this.attributes = createAttributeBindings(el, attributes).mount(parentScope);
            this.props = {
              ...evaluateInitialProps(el, props),
              ...evaluateAttributeExpressions(this.attributes.expressions)
            };
            this.state = this.getMappedState();
            this.actions = this.getMappedActions();
            this.unsubscribe = store.subscribe(() => {
              this.update();
            });
            this.component = component(Component)(this.el, {}, { attributes: [attr], parentScope: this, slots });
            this.isMounted = true;
          } catch (error) {
            if (runError) {
              options.onMountError?.(error, Component.name);
            }
          }
        },

        update(...args) {
          try {
            // @ts-ignore
            const parentScope = args.length === 2 ? args[1] : args[0];
            if (parentScope) {
              this.attributes?.update(parentScope);
            }
            const props = {
              ...this.props,
              ...evaluateAttributeExpressions(this.attributes?.expressions || [])
            };
            const useProps = mapStateToProps?.length === 2 || mapDispatchToProps?.length === 2;
            const isPropsEqual = useProps ? shallowEqual(props, this.props) : true;
            this.props = props;
            const state = this.getMappedState();
            const isStateEqual = shallowEqual(state, this.state);
            this.state = state;
            const actions = this.getMappedActions();
            this.actions = actions;
            if (isStateEqual && isPropsEqual) {
              return;
            }
            if (!this.isMounted) {
              return;
            }
            this.component?.update({}, this);
          } catch (error) {
            if (runError) {
              options.onMountError?.(error, Component.name);
            }
          }
        },

        unmount(preserveRoot, meta = {}) {
          try {
            this.unsubscribe?.();
            this.attributes?.unmount();
            this.component?.unmount(preserveRoot);
            this.isMounted = false;
            if (meta.keepElement && meta.cleanAttributes) {
              Object.keys(this.state).forEach(key => this.el.removeAttribute(key));
            }
            if (meta.keepElement || this.el.parentNode == null) {
              cleanNode(this.el);
            } else {
              removeChild(this.el);
            }
          } catch (error) {
            if (runError) {
              options.onMountError?.(error, Component.name);
            }
          }
        }
      })
    )
  };
}

function preventError(cb) {
  runError = false;
  cb();
  runError = true;
}


/**
 * Executes a simple request and dispatches its lifecycle actions.
 *
 * This helper is intended for straightforward request flows. Do not force
 * complex branching, recovery, retry, or multi-step logic into this API.
 * Use a raw try-catch-finally block for complex control flow.
 *
 * Without options, the behavior is identical to the original implementation:
 * - requestStart is called without arguments.
 * - The resolved response is returned on success.
 * - undefined is returned after a handled failure.
 * - No finally callback is executed.
 *
 * @template TResult
 * @template TError
 * @template TSuccessReturn
 * @template TFailureReturn
 *
 * @param {() => TResult | PromiseLike<TResult>} func
 *   The request function to execute.
 * @param {(action: unknown) => unknown} dispatch
 *   The dispatch function.
 * @param {(...args: unknown[]) => unknown} requestStart
 *   Creates the request-start action.
 * @param {(response: TResult) => unknown} requestSuccess
 *   Creates the request-success action.
 * @param {(error: TError) => unknown} requestFailure
 *   Creates the request-failure action.
 * @param {Object} [options]
 *   Optional execution settings.
 * @param {unknown[]} [options.requestStartArgs]
 *   Arguments spread into requestStart. Defaults to an empty array.
 * @param {'response' | 'undefined' | 'null' | 'status'
 *   | ((response: TResult) =>
 *       TSuccessReturn | PromiseLike<TSuccessReturn>)
 * } [options.successReturn='response']
 *   Controls the value returned after a successful request.
 * @param {'undefined' | 'error' | 'null' | 'status' | 'throw'
 *   | ((error: TError) =>
 *       TFailureReturn | PromiseLike<TFailureReturn>)
 * } [options.failureReturn='undefined']
 *   Controls the value returned after a failed request.
 *   The "throw" preset rethrows the caught error.
 * @param {() => void | PromiseLike<void>} [options.finallyCallback]
 *   Runs from the finally block after success or failure handling.
 *
 * @returns {Promise<TResult | TError | TSuccessReturn | TFailureReturn | null | true | false | undefined>
 * }
 *
 * @throws {TypeError}
 *   If an option has an unsupported type or preset value.
 */
async function performRequest(
  func,
  dispatch,
  requestStart,
  requestSuccess,
  requestFailure,
  options,
) {
  const {
    requestStartArgs,
    successReturn = 'response',
    failureReturn = 'undefined',
    finallyCallback,
  } = options ?? {};

  dispatch(
    requestStartArgs == null
      ? requestStart() : Array.isArray(requestStartArgs)
        ? requestStart(...requestStartArgs)
        : requestStart(requestStartArgs),
  );

  try {
    const response = await func();
    dispatch(requestSuccess(response));

    if (typeof successReturn === 'function') {
      return await successReturn(response);
    }
    switch (successReturn) {
      case 'response':
        return response;
      case 'null':
        return null;
      case 'status':
        return true;
      default:
        return undefined;
    }
  } catch (/** @type {any} */ error) {
    dispatch(requestFailure(error));

    if (typeof failureReturn === 'function') {
      return await failureReturn(error);
    }

    switch (failureReturn) {
      case 'error':
        return error;
      case 'throw':
        throw error;
      case 'null':
        return null;
      case 'status':
        return false;
      default:
        return undefined;
    }
  } finally {
    await finallyCallback?.();
  }
}

export {
  registerStore,
  connect,
  performRequest,
  evaluateInitialProps,
  evaluateAttributeExpressions,
  createAttributeBindings,
  preventError,
};